import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer
} from 'react';
import { UserProfile } from '@partnerconnect-monorepo/types/user';
import {
  getUserProfile,
  useFlaUserProfile,
  useReportingUserProfile
} from '../../api/user';
import { isWfpUser } from '../../helpers/helpers';
import { getUserNameFromToken } from '../../helpers/user';
import { useAuth } from 'react-oidc-context';
import { isEqual } from 'lodash';

type Action =
  | {
      type: 'setUserProfile';
      userProfile: UserProfile;
    }
  | {
      type: 'stopLoading';
    };

type Dispatch = (action: Action) => void;

interface State {
  userProfile: UserProfile | null;
  isWfpUser: boolean;
  isLoading: boolean;
  username: string;
}

export interface UserProfileContextInterface extends State {
  userProfileDispatch: Dispatch;
}

const initialValues: State = {
  userProfile: null,
  isWfpUser: false,
  isLoading: true,
  username: ''
};

function userProfileReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'setUserProfile': {
      return {
        ...state,
        userProfile: action.userProfile,
        isWfpUser: isWfpUser(action.userProfile?.roles),
        isLoading: false,
        username:
          action.userProfile.firstName && action.userProfile.lastName
            ? `${action.userProfile?.firstName} ${action.userProfile?.lastName}`
            : getUserNameFromToken() || state.username
      };
    }
    case 'stopLoading': {
      return {
        ...state,
        isLoading: false
      };
    }
    default:
      return state;
  }
}

export const UserProfileContext = createContext<
  UserProfileContextInterface | undefined
>(undefined);

function UserProfileProvider({
  performAuth,
  children
}: PropsWithChildren<{ performAuth?: boolean }>) {
  const { user } = useAuth();

  const [{ userProfile, isLoading, isWfpUser, username }, dispatch] =
    useReducer(
      (state: State, action: Action) => userProfileReducer(state, action),
      {
        ...initialValues,
        username: user?.profile.given_name ?? initialValues.username
      }
    );

  const {
    data: reportingUserProfile,
    isSuccess: isReportingUserProfileFetched
  } = useReportingUserProfile();

  const { data: flaUserProfile, isSuccess: isFlaUserProfileFetched } =
    useFlaUserProfile();

  const authenticate = useCallback(() => {
    if (user?.id_token && !user.expired) {
      const fetchedUserProfile = getUserProfile(
        reportingUserProfile && Object.keys(reportingUserProfile).length
          ? reportingUserProfile
          : null,
        flaUserProfile && Object.keys(flaUserProfile).length
          ? flaUserProfile
          : null
      );

      if (fetchedUserProfile) {
        !isEqual(userProfile, fetchedUserProfile) &&
          dispatch({
            type: 'setUserProfile',
            userProfile: fetchedUserProfile
          });
        return;
      } else {
        dispatch({ type: 'stopLoading' });
        return;
      }
    } else {
      dispatch({ type: 'stopLoading' });
      return;
    }
  }, [
    user?.expired,
    user?.id_token,
    userProfile,
    reportingUserProfile,
    flaUserProfile
  ]);

  useEffect(() => {
    if (
      performAuth &&
      isReportingUserProfileFetched &&
      isFlaUserProfileFetched
    ) {
      authenticate();
    }
  }, [
    performAuth,
    authenticate,
    isReportingUserProfileFetched,
    isFlaUserProfileFetched
  ]);

  const value: UserProfileContextInterface = {
    userProfile,
    isWfpUser,
    isLoading,
    userProfileDispatch: dispatch,
    username
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}

function useUserProfile() {
  const context = useContext(UserProfileContext);

  if (context === undefined) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }

  return context;
}

export { UserProfileProvider, useUserProfile };
